import cls from "./profileDropdown.module.scss";
import PopoverContainer from "containers/popover/popover";
import { IUser } from "interfaces/user.interface";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import usePopover from "hooks/usePopover";
import { useState } from "react";
import FallbackImage from "components/fallbackImage/fallbackImage";
import getAvatar from "utils/getAvatar";
import getProfileIdentity, { getProfilePhotoSource } from "utils/getProfileIdentity";
import CloseLineIcon from "remixicon-react/CloseLineIcon";
import { profileDropdownLinks } from "./profileDropdownLinks";
import { useAuth } from "contexts/auth/auth.context";
import LogoutCircleRLineIcon from "remixicon-react/LogoutCircleRLineIcon";
import Wallet3LineIcon from "remixicon-react/Wallet3LineIcon";
import Price from "components/price/price";
import { getNearestFloor } from "utils/getNearestFloor";

type Props = {
  data: IUser;
};

function ProfileAvatar({ data, initials }: Props & { initials: string }) {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const resolvedSource = typeof data?.img === "string" && data.img.trim() ? getAvatar(data.img.trim()) : null;
  const source = getProfilePhotoSource(resolvedSource, [
    process.env.NEXT_PUBLIC_API_HOSTNAME,
    process.env.NEXT_PUBLIC_STORAGE_HOSTNAME,
  ], process.env.NEXT_PUBLIC_PROTOCOL);

  return source && source !== failedSource ? (
    <FallbackImage
      fill
      src={source}
      alt=""
      sizes="60px"
      onError={() => setFailedSource(source)}
    />
  ) : <span className={cls.initials} aria-hidden="true">{initials}</span>;
}

export default function ProfileDropdown({ data }: Props) {
  const { t } = useTranslation();
  const [open, anchorEl, handleOpen, handleClose] = usePopover();

  const { logout, isAuthenticated, openAuthModal, openWalletHistory, user } =
    useAuth();
  const identity = getProfileIdentity(data, t("profile"));
  const handleLogout = () => {
    logout();
    handleClose();
  };

  const actualLinks = !isAuthenticated
    ? profileDropdownLinks.filter((item) => !item.requiredAuth)
    : profileDropdownLinks;

  if (!isAuthenticated) {
    return (
      <button
        type="button"
        className={cls.login}
        onClick={() => openAuthModal()}
      >
        {t("login")}
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className={cls.profileBtn}
        onClick={handleOpen}
        aria-label={identity.name}
        aria-expanded={open}
        title={identity.name}
      >
        <div className={cls.imgWrapper}>
          <ProfileAvatar data={data} initials={identity.initials} />
        </div>
        <span className={cls.profileName}>{identity.name}</span>
      </button>

      <PopoverContainer
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: 40 }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className={cls.wrapper}>
          <button type="button" className={cls.closeIcon} onClick={handleClose}>
            <CloseLineIcon />
          </button>
          <header className={cls.header}>
            <div className={cls.profileImage}>
              <ProfileAvatar data={data} initials={identity.initials} />
            </div>
            <div className={cls.naming}>
              <Link href={"/profile"} className={cls.title}>
                {identity.name}
              </Link>
            </div>
          </header>
          <div className={cls.body}>
            <button
              type="button"
              className={cls.flex}
              key="wallet"
              onClick={() => {
                handleClose();
                openWalletHistory();
              }}
            >
              <Wallet3LineIcon size={24} />
              <div className={cls.text}>
                {t("wallet")}:{" "}
                <span className={cls.price}>
                  <Price number={getNearestFloor(user?.wallet?.price)} />
                </span>
              </div>
            </button>
            {actualLinks.map((item) => (
              <Link
                href={item.href}
                className={cls.flex}
                key={item.href}
                onClick={handleClose}
              >
                {item.icon}
                <span className={cls.text}>{t(item.label)}</span>
              </Link>
            ))}
            <button type="button" className={cls.flex} onClick={handleLogout}>
              <LogoutCircleRLineIcon size={24} />
              <span className={cls.text}>{t("logout")}</span>
            </button>
          </div>
        </div>
      </PopoverContainer>
    </>
  );
}
